/**
 * WhatsNewPanel — small dismissible "release notes" card for the most
 * recently shipped user-facing changes. Mounted on the Dashboard so
 * users see it the first time they log in after a deploy.
 *
 * Each entry in `WHATS_NEW_ENTRIES` has a stable `key` that's persisted
 * to the user document when dismissed (`dismissed_whats_new_keys`).
 * The panel renders the *first* entry the current user hasn't already
 * dismissed AND whose `audience` matches them — so we never stack
 * multiple announcements at once, and adding a new entry doesn't
 * re-surface stale ones.
 *
 * Adding a new announcement:
 *   1. Prepend a new entry with a unique `key` (e.g. `2026-03-foo`).
 *   2. Deploy. Users see it once each, then dismiss. Done.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Sparkles, X } from 'lucide-react';

// Newest first. Each entry is shown at most once per user.
//   audience: 'all' | 'instructor' | 'admin'
const WHATS_NEW_ENTRIES = [
  {
    key: '2026-02-bulk-import-pricing-clarity',
    audience: 'instructor',
    title: 'Bulk Upload now has a clearer price column',
    body:
      "We renamed cost_description → price and moved it up to column 4 so it's hard to miss. "
      + "Paid events without a price filled in now flag a warning in the preview step.",
    cta: { label: 'Open Bulk Upload', href: '/events/bulk-import' },
  },
  {
    key: '2026-02-bulk-import-launch',
    audience: 'instructor',
    title: 'New: Bulk upload events from a spreadsheet',
    body:
      'Save time by importing many events at once. Download the template, fill in your '
      + 'events (including Charity Events!), and upload — we validate every row before '
      + 'anything goes live.',
    cta: { label: 'Try Bulk Upload', href: '/events/bulk-import' },
  },
  {
    key: '2026-02-duplicate-event-free-posts',
    audience: 'all',
    title: 'Banked free event posts now auto-apply when you duplicate',
    body:
      "Got free posts from a coupon? They'll auto-apply to duplicated events too. "
      + "You'll see the banner inside the editor so you know you won't be charged.",
  },
];

export default function WhatsNewPanel() {
  const { user, refreshUser } = useAuth();
  const [dismissing, setDismissing] = useState(false);

  if (!user) return null;

  const dismissed = new Set(user.dismissed_whats_new_keys || []);
  const isInstructor = !!user.is_instructor || user.role === 'admin';
  const isAdmin = user.role === 'admin';
  const matchesAudience = (entry) => {
    if (entry.audience === 'all') return true;
    if (entry.audience === 'instructor') return isInstructor;
    if (entry.audience === 'admin') return isAdmin;
    return false;
  };
  const next = WHATS_NEW_ENTRIES.find(e => !dismissed.has(e.key) && matchesAudience(e));
  if (!next) return null;

  const dismiss = async () => {
    setDismissing(true);
    try {
      await api.post(`/profile/dismiss-whats-new/${next.key}`);
      // Best-effort refresh so the panel disappears without a full reload.
      if (typeof refreshUser === 'function') await refreshUser();
    } catch (e) {
      // Silently fail — worst case the user sees the panel again on next load.
    } finally {
      setDismissing(false);
    }
  };

  return (
    <div
      data-testid="whats-new-panel"
      className="mb-6 flex items-start gap-3 p-4 rounded-2xl bg-gradient-to-r from-[#b456df]/10 to-[#56dfb4]/10 border border-[#b456df]/30"
    >
      <div className="w-10 h-10 rounded-xl bg-[#b456df]/15 flex items-center justify-center flex-shrink-0">
        <Sparkles className="w-5 h-5 text-[#b456df]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-[#7a2faa] mb-0.5" data-testid="whats-new-title">{next.title}</p>
        <p className="text-sm text-slate-600">{next.body}</p>
        {next.cta && (
          <Link to={next.cta.href}>
            <Button
              data-testid="whats-new-cta"
              className="mt-3 rounded-full bg-[#b456df] hover:bg-[#9a3ec4] text-white px-4 py-1.5 h-auto text-sm"
            >
              {next.cta.label}
            </Button>
          </Link>
        )}
      </div>
      <button
        onClick={dismiss}
        disabled={dismissing}
        data-testid="whats-new-dismiss"
        aria-label="Dismiss this announcement"
        className="text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0 p-1"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
